import Interop from "../interfaces/Interop";
import { ModuleType } from "../enums/ModuleType";
import { ApplicationColor } from "../models/ApplicationColor";
import { ChatPageNavigation } from "../models/ChatPageNavigation";
import { ClientCredential } from "../models/ClientCredential";
import { ChannelDataModel } from "../models/ChannelDataModel";
import { ChatPageType } from "../enums/ChatPageType";

export default class WebInterop implements Interop {
    /**
     * Map of message types to response handler functions
     * Stores pending promises waiting for responses from parent app
     * 
     * KEY: Message type (e.g., "GetCredentials", "GetColors")
     * VALUE: Callback function to resolve promise with response data
     */
    public responseHandlers: Map<string, (value: any) => void> = new Map();
    
    /**
     * PROPERTY: pwaEventOrigin
     * 
     * Trusted origin for Blazor PWA parent app
     * Only messages from this origin will be accepted
     * 
     * SECURITY: Prevents malicious sites from sending fake responses
     */
    private pwaEventOrigin = process.env.REACT_APP_PWA_ORIGIN || "";
    
    /**
     * PROPERTY: vueEventOrigin
     * 
     * Trusted origin for Vue.js parent app
     * Allows same React app to work with different parent frameworks
     */
    private vueEventOrigin = process.env.REACT_APP_VUE_ORIGIN || "";
    
    /**
     * CONSTRUCTOR
     * 
     * Initializes WebInterop by setting up message event listener
     * 
     * FLOW:
     * 1. Constructor called when WebInterop instance created (index.tsx)
     * 2. Calls initMessageListener to attach event handler
     * 3. Now ready to send/receive messages with parent app
     */
    constructor() {
        this.initMessageListener();
    }

    /**
     * METHOD: initMessageListener
     * 
     * Attaches message event listener to window
     * 
     * LISTENER:
     * Calls handleMessage method when parent app sends message
     * 
     * EXECUTION: Called once in constructor
     */
    public initMessageListener() {
        window.addEventListener("message", this.handleMessage);
    }

    /**
     * METHOD: FinishedLoading
     * 
     * No-op for web platform
     * Native apps need this notification, but web apps don't
     */
    FinishedLoading(): void {
    }
    
    /**
     * METHOD: GetLanguage
     * 
     * Fetches user's language preference from parent web app
     * 
     * FLOW:
     * 1. Send "GetLanguage" request to parent
     * 2. Parent responds with language code string
     * 3. Return language code (e.g., "en", "fr", "da")
     * 
     * EXECUTION: Runs once during app initialization (BootComponent)
     */
    async GetLanguage(): Promise<string> {
        const languageCode = await this.sendMessageAndWaitForResponse("GetLanguage", String);
        return Promise.resolve(languageCode);
    }
    
    /**
     * METHOD: ToogleMenu
     * 
     * No-op for web platform
     * Web apps handle participant selection within React UI
     * Native apps use native participant picker
     */
    ToogleMenu(): void {
    }

    /**
     * METHOD: SlidingGesture
     * 
     * No-op for web platform
     * Swipe gestures are native mobile feature only
     */
    SlidingGesture(enable: boolean): void {
    }

    /**
     * METHOD: GetFontSize
     * 
     * Returns default font size for web platform
     * 
     * RETURNS: 100 (100% - normal size)
     * 
     * NOTE: Web apps could extend this to read from parent app settings
     */
    GetFontSize(): Promise<Number> {
        return Promise.resolve(100);
    }

    /**
     * METHOD: GetCredentials
     * 
     * Fetches user authentication credentials from parent web app
     * 
     * FLOW:
     * 1. Send "GetCredentials" request to parent
     * 2. Parent responds with JSON object
     * 3. Parse response and extract fields
     * 4. Create and return ClientCredential instance
     * 
     * RESPONSE FIELDS:
     * - UserId: User's unique identifier
     * - UserToken: JWT authentication token for Stream Chat
     * - TeamId: Team/organization identifier
     * - EventId: Current event ID (parsed to number)
     * - EventName: Name of current event
     * - GroupIds: Array of user's group memberships
     * 
     * EXECUTION: Runs once during app initialization (BootComponent)
     */
    async GetCredentials(): Promise<ClientCredential> {
        const response = await this.sendMessageAndWaitForResponse("GetCredentials", JSON.parse);
        const { UserId, UserToken, TeamId, EventId, EventName,  GroupIds} = response;
        return new ClientCredential(UserId, UserToken, TeamId, Number.parseInt(EventId), EventName, GroupIds );
    }

    /**
     * METHOD: GetColors
     * 
     * Fetches app theme colors from parent web app
     * 
     * FLOW:
     * 1. Send "GetColors" request to parent
     * 2. Parent responds with JSON object
     * 3. Parse response and extract color fields
     * 4. Create and return ApplicationColor instance
     * 
     * RESPONSE FIELDS:
     * - TopNavBackgroundColor: Header background color (hex)
     * - TopNavForegroundColor: Header text color (hex)
     * - CtaBackgroundColor: Button background color (hex)
     * - CtaFontColor: Button text color (hex)
     * 
     * USAGE: Colors are applied as CSS variables to theme the entire app
     * EXECUTION: Runs once during app initialization (BootComponent)
     */
    async GetColors(): Promise<ApplicationColor> {
        const response = await this.sendMessageAndWaitForResponse("GetColors", JSON.parse);
        const { TopNavBackgroundColor, TopNavForegroundColor,CtaBackgroundColor,CtaFontColor } = response;
        return new ApplicationColor(TopNavBackgroundColor, TopNavForegroundColor,CtaBackgroundColor,CtaFontColor);
    }

    /**
     * METHOD: GetPageType
     * 
     * Fetches navigation context from parent web app
     * Determines what page to show and which channel to open (if any)
     * 
     * FLOW:
     * 1. Send "GetPageType" request to parent
     * 2. Parent responds with JSON object
     * 3. Parse response and extract navigation fields
     * 4. Create and return ChatPageNavigation instance
     * 
     * EXECUTION: Runs once during app initialization (BootComponent)
     */
    async GetPageType(): Promise<ChatPageNavigation> {
        const pageTypeResponse = await this.sendMessageAndWaitForResponse("GetPageType", JSON.parse);
        const { ChatPageType, EntityType, EntityId, ChannelHashId, PushMessageId, OpenDMTab, DMTabName, EventChatName } = pageTypeResponse;
        return new ChatPageNavigation(ChatPageType, EntityType, EntityId, ChannelHashId, PushMessageId, OpenDMTab, DMTabName, EventChatName);
    }

    /**
     * METHOD: SendNewMessageUpdate
     * 
     * Sends new message data to parent web app
     * 
     * PARAMETERS:
     * - channelData: ChannelDataModel containing message details
     * 
     * FLOW:
     * 1. Stringify channelData to JSON
     * 2. Send "SendNewMessageUpdate" message to parent
     * 3. Parent can update UI, show notifications, etc.
     */
    SendNewMessageUpdate(channelData: ChannelDataModel): void {
        this.sendMessage("SendNewMessageUpdate", JSON.stringify(channelData));
    }

    /**
     * METHOD: NavigationHandle
     * 
     * No-op for web platform
     * Navigation behavior is handled by web app routing
     */
    NavigationHandle(enable: boolean): void {
    }

    /**
     * METHOD: PushHandle
     * 
     * No-op for web platform
     * Push notification handling is managed by parent web app
     */
    PushHandle(ReceiverUserId: string): void {
    }

    /**
     * METHOD: sendMessage
     * 
     * Sends message to parent web app via postMessage
     * 
     * PARAMETERS:
     * - type: Message type identifier (e.g., "GetCredentials", "SendNewMessageUpdate")
     * - channelData: Optional data payload (already stringified)
     * 
     * BEHAVIOR:
     * Sends message to BOTH pwaEventOrigin and vueEventOrigin
     * This allows same React app to work with different parent apps
     * Only the active parent will receive and process the message
     * 
     * MESSAGE STRUCTURE:
     * { type: "MessageType", channelData: "optional-json-string" }
     * 
     * SECURITY:
     * Uses targetOrigin parameter to ensure messages only go to trusted origins
     */
    public sendMessage(type: string, channelData?: string) {
        const message: { type: string; channelData?: string } = { type };

        if (channelData) {
            // Double-stringify: channelData is already string, wrap in message.channelData
            message.channelData = JSON.stringify(channelData);
        }

        // Send to both possible parent apps (only active one will respond)
        window.parent.postMessage(message, this.pwaEventOrigin+"/");
        window.parent.postMessage(message, this.vueEventOrigin+"/");
    }
    
    /**
     * METHOD: sendMessageAndWaitForResponse
     * 
     * Sends message to parent app and waits for response (async request-response pattern)
     * 
     * PARAMETERS:
     * - type: Message type identifier
     * - transform: Function to transform response data (e.g., JSON.parse, String)
     * 
     * FLOW:
     * 1. Create promise
     * 2. Create response handler function
     * 3. Store handler in responseHandlers map with type as key
     * 4. Send message to parent
     * 5. Parent processes request
     * 6. Parent sends response back
     * 7. handleMessage receives response
     * 8. handleMessage finds handler in map and calls it
     * 9. Handler transforms data and resolves promise
     * 10. Handler removes itself from map (cleanup)
     * 
     * ERROR HANDLING:
     * - If transform throws error → promise rejects
     * - Handler still removed from map to prevent memory leak
     * 
     * RETURNS:
     * Promise that resolves with transformed response data
     * 
     * GENERIC TYPE:
     * T is the return type after transformation
     */
    public sendMessageAndWaitForResponse<T>(type: string, transform: (data: any) => T): Promise<T> {
        return new Promise((resolve, reject) => {
            const handler = (data: any) => {
                try {
                    resolve(transform(data));
                } catch (error) {
                    reject(error);
                } finally {
                    // Always cleanup handler to prevent memory leak
                    this.responseHandlers.delete(type);
                }
            };
            // Store handler for later invocation
            this.responseHandlers.set(type, handler);
            // Send request
            this.sendMessage(type);
        });
    }

    /**
     * METHOD: handleMessage
     * 
     * Receives messages from parent web app
     * 
     * PARAMETERS:
     * - event: MessageEvent from window.addEventListener("message")
     * 
     * SECURITY CHECK:
     * Only processes messages from trusted origins
     * Ignores messages from other sources (prevents XSS attacks)
     * 
     * FLOW:
     * 1. Check if event.origin is trusted (pwaEventOrigin or vueEventOrigin)
     * 2. Extract type and data from event.data
     * 3. Look up response handler for this message type
     * 4. If handler exists → call it with data (resolves waiting promise)
     * 5. If no handler → ignore message (might be for other listeners)
     * 
     * MESSAGE FORMAT:
     * event.data = { type: "MessageType", data: responsePayload }
     */
    public handleMessage = (event: MessageEvent) => {
        // Security: Only accept messages from trusted origins
        if (event.origin === this.pwaEventOrigin || event.origin === this.vueEventOrigin) {
            const { type, data } = event.data;
            const handler = this.responseHandlers.get(type);
            if (handler) {
                handler(data);
            }
        }
    };

    /**
     * METHOD: GetPlatformType
     * 
     * Returns platform type for web browser
     * 
     * RETURNS: "BlazorPWA" (identifies web platform vs mobile)
     * 
     * USAGE:
     * Determines UI behavior and feature availability
     * Different from native platforms which return "Mobile" or "Desktop"
     */
    GetPlatformType(): Promise<string> {
        return Promise.resolve("BlazorPWA");
    }
}


